import { Fragment } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Icon } from '../core/Icon';

export type TimelineTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger';

const tones: Record<TimelineTone, { bg: string; fg: string; bd: string }> = {
    neutral: { bg: 'var(--surface-secondary)', fg: 'var(--neutral-600)', bd: 'var(--border)' },
    brand: { bg: 'var(--surface-brand-soft)', fg: 'var(--text-brand)', bd: 'var(--border-brand-soft)' },
    success: { bg: 'var(--success-soft)', fg: 'var(--success-strong)', bd: '#C8ECD6' },
    warning: { bg: 'var(--warning-soft)', fg: 'var(--warning-strong)', bd: '#F3E0B4' },
    danger: { bg: 'var(--danger-soft)', fg: 'var(--danger-strong)', bd: '#F7CDD3' },
};

export interface TimelineMeta {
    label: string;
    value: ReactNode;
}

export interface TimelineEvent {
    /** Identificador estável. Sem ele, o índice é usado como último recurso. */
    id?: string | number;
    title?: ReactNode;
    time?: ReactNode;
    author?: ReactNode;
    description?: ReactNode;
    icon?: string;
    tone?: TimelineTone;
    meta?: TimelineMeta[];
}

export interface TimelineProps {
    events?: TimelineEvent[];
    style?: CSSProperties;
}

export function Timeline({ events = [], style }: TimelineProps) {
    return (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, position: 'relative', ...style }}>
            {events.map((e, i) => {
                const t = tones[e.tone ?? 'neutral'] ?? tones.neutral;
                const ultimo = i === events.length - 1;
                return (
                    <li key={e.id ?? i} style={{ display: 'flex', gap: 12, position: 'relative', paddingBottom: ultimo ? 0 : 20 }}>
                        {!ultimo && <span aria-hidden="true" style={{ position: 'absolute', left: 13, top: 28, bottom: 0, width: 1, background: 'var(--border)' }} />}
                        <span style={{ width: 27, height: 27, flex: '0 0 auto', borderRadius: '50%', background: t.bg, border: `1px solid ${t.bd}`, color: t.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                            <Icon name={e.icon ?? 'circle'} size={13} />
                        </span>
                        <div style={{ flex: 1, minWidth: 0, paddingTop: 3 }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{e.title}</span>
                                <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{e.time}</span>
                                {e.author && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>· {e.author}</span>}
                            </div>
                            {e.description && <p style={{ margin: '3px 0 0', fontSize: 13, lineHeight: '19px', color: 'var(--text-secondary)' }}>{e.description}</p>}
                            {e.meta && (
                                <dl style={{ margin: '8px 0 0', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: 12.5 }}>
                                    {e.meta.map((m) => (
                                        <Fragment key={m.label}>
                                            <dt style={{ color: 'var(--text-tertiary)' }}>{m.label}</dt>
                                            <dd style={{ margin: 0, color: 'var(--neutral-700)', fontWeight: 500 }}>{m.value}</dd>
                                        </Fragment>
                                    ))}
                                </dl>
                            )}
                        </div>
                    </li>
                );
            })}
        </ol>
    );
}
